import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Définition du type pour le contexte
interface AudioPlayerContextType {
  play: (url?: string) => void;
  pause: () => void;
  isPlaying: boolean;
  currentUrl: string | null;
  setCurrentTrack: (track: { audioUrl: string; title?: string; id?: string }) => void;
  isMuted: boolean;
  toggleMute: () => void;
  currentTrack: { audioUrl: string; title?: string; id?: string } | null;
}

// Création du contexte
const AudioPlayerContext = createContext<AudioPlayerContextType | null>(null);

// Hook personnalisé pour utiliser le contexte
export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error("useAudioPlayer doit être utilisé dans un AudioPlayerProvider");
  }
  return context;
};

// Provider du contexte
export function AudioPlayerProvider({ children }: { children: React.ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTrack, setCurrentTrackState] = useState<{ audioUrl: string; title?: string; id?: string } | null>(null);
  const [audio] = useState(() => {
    const audio = new Audio();
    audio.loop = true;
    return audio;
  });

  // Déplacer la logique de lecture dans un useEffect
  useEffect(() => {
    if (!currentTrack) return;

    const cleanupEvents = () => {
      audio.removeEventListener('ended', () => {});
      audio.removeEventListener('error', () => {});
    };

    cleanupEvents();
    audio.pause();
    
    if (audio.src !== currentTrack.audioUrl) {
      audio.src = currentTrack.audioUrl;
      audio.load();
    }

    const handleError = (error: Event) => {
      console.error("Erreur de l'élément audio:", error);
      setIsPlaying(false);
    };

    audio.addEventListener('error', handleError);

    return () => {
      cleanupEvents();
      audio.removeEventListener('error', handleError);
    };
  }, [currentTrack, audio]);

  const setCurrentTrack = (track: { audioUrl: string; title?: string; id?: string }) => {
    console.log("Setting new track:", track);
    setCurrentUrl(track.audioUrl);
    setCurrentTrackState(track);
  };

  const play = useCallback((url?: string) => {
    if (url) {
      if (url === currentUrl) {
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => setIsPlaying(true))
            .catch(error => {
              if (error.name !== 'AbortError') {
                console.error("Erreur lors de la lecture:", error);
                setIsPlaying(false);
              }
            });
        }
      } else {
        setCurrentTrack({ audioUrl: url });
      }
    } else if (currentUrl) {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch(error => {
            if (error.name !== 'AbortError') {
              console.error("Erreur lors de la lecture:", error);
              setIsPlaying(false);
            }
          });
      }
    }
  }, [currentUrl, audio]);

  const pause = useCallback(() => {
    audio.pause();
    setIsPlaying(false);
  }, [audio]);

  const toggleMute = useCallback(() => {
    audio.muted = !audio.muted;
    setIsMuted(!isMuted);
  }, [audio, isMuted]);

  return (
    <AudioPlayerContext.Provider
      value={{
        play,
        pause,
        isPlaying,
        currentUrl,
        setCurrentTrack,
        isMuted,
        toggleMute,
        currentTrack
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
} 